import { map, switchMap } from 'rxjs/operators';
import { Injectable, OnInit } from '@angular/core';
import { Observable } from 'rxjs';
import { Inspection, InspectionSection } from '../models/index';
import { ApiService } from './api.service';
import { DataService } from './data.service';
import { BaseService } from './baseService';
import { InspectionFactoryService } from './inspectionFactory.service';

@Injectable({
  providedIn: 'root'
})
export class InspectionSectionService extends BaseService {
  constructor(private apiService: ApiService, private dataService: DataService, private factory: InspectionFactoryService) {
    super();
  }

  public getSection(inspectionId: string, sectionId: string): Observable<InspectionSection> {
    return this.dataService.getSection(inspectionId, sectionId);
  }

  public getSections(inspectionId: string): Observable<InspectionSection[]> {
    return this.dataService.getSections(inspectionId);
  }

  public addPoints(
    id: string,
    targetSectionId: string,
    sourceInspectionId: string,
    sourcePointsId: string[],
    targetFolderId: string
  ): Observable<Inspection> {
    return this._addPoints(id, targetSectionId, sourceInspectionId, sourcePointsId, targetFolderId).pipe(
      map((inspection: Inspection) => {
        this.dataService.processInspection(inspection);
        return this.dataService.getInspection(id);
      })
    );
  }

  public addPoint(id: string, targetSectionId: string, sourceInspectionId: string, sourcePointId: string): Observable<Inspection> {
    const url = `/inspections/${id}/points`;
    return this.apiService
      .post(url, {
        id: id,
        sourceInspectionId: sourceInspectionId,
        targetSectionId: targetSectionId,
        sourcePointId: sourcePointId
      })
      .pipe(
        map((inspection: Inspection) => {
          return inspection;
        })
      );
  }

  public newSection(id: string, model: any): Observable<InspectionSection> {
    return this._newSection(id, model).pipe(
      map((section: InspectionSection) => {
        return this.dataService.updateSection(id, section);
      })
    );
  }

  public orderItems(id: string, sectionId: string, orderableItems: any) {
    return this._orderItems(id, sectionId, orderableItems).pipe(
      map((section: InspectionSection) => {
        return this.dataService.updateSectionPointsOrder(id, section, orderableItems);
      })
    );
  }

  public deleteSections(id: string, sectionIds: Array<string>): any {
    return this._deleteSections(id, sectionIds).pipe(
      map((res: any) => {
        if (res.sectionsDelete) {
          this.dataService.removeSection(id, sectionIds);
        }
        const inspection = this.dataService.getInspection(id);
        inspection.progression = res.inspectionProgression;
        return res.sectionsDelete;
      })
    );
  }

  public deleteSection(id: string, sectionId: string): any {
    return this._deleteSection(id, sectionId).pipe(
      map((res: any) => {
        if (res.sectionDelete) {
          this.dataService.removeSection(id, [sectionId]);
        }
        const inspection = this.dataService.getInspection(id);
        inspection.progression = res.inspectionProgression;
        return res.sectionDelete;
      })
    );
  }

  public updateProperties(inspectionId: string, id: string, params: any): Observable<InspectionSection> {
    return this._updateProperties(inspectionId, id, params).pipe(
      map((section: InspectionSection) => {
        this.dataService.updateSectionProperties(inspectionId, id, params);
        return section;
      })
    );
  }

  private _updateProperties(inspectionId: string, sectionId: string, params: any): Observable<InspectionSection> {
    const url = `/inspections/${inspectionId}/sections/${sectionId}`;
    if (this.isInspectionInOffline(inspectionId)) {
      return this.factory.updateSection(inspectionId, sectionId, params);
    }
    return this.apiService.patch(url, params);
  }

  private _orderItems(inspectionId: string, sectionId: string, orderableItems: any): Observable<InspectionSection> {
    const url = `/inspections/${inspectionId}/sections/${sectionId}/items/order`;
    if (this.isInspectionInOffline(inspectionId)) {
      return this.factory.orderPoints(inspectionId, sectionId, orderableItems);
    }
    return this.apiService.post(url, { OrderableItems: orderableItems });
  }

  private _deleteSection(inspectionId: string, sectionId: string): any {
    const url = `/inspections/${inspectionId}/sections/${sectionId}`;
    if (this.isInspectionInOffline(inspectionId)) {
      return this.factory.deleteSection(inspectionId, sectionId);
    }
    return this.apiService.delete(url);
  }

  private _deleteSections(inspectionId: string, sectionIds: Array<string>): any {
    const url = `/inspections/${inspectionId}/sections/delete`;
    if (this.isInspectionInOffline(inspectionId)) {
      return this.factory.deleteSections(inspectionId, sectionIds);
    }
    return this.apiService.post(url, sectionIds);
  }

  private _newSection(inspectionId: string, model: any): Observable<InspectionSection> {
    const url = `/inspections/edit/${inspectionId}/sections/postnew`;
    if (this.isInspectionInOffline(inspectionId)) {
      return this.factory.createSection(inspectionId, model);
    }
    return this.apiService.post(url, model);
  }

  private _addPoints(
    id: string,
    targetSectionId: string,
    sourceInspectionId: string,
    sourcePointsId: string[],
    targetFolderId: string
  ): Observable<Inspection> {
    const url = `/inspections/${id}/points`;
    const data = {
      id,
      sourceInspectionId,
      targetSectionId,
      sourcePointsId,
      targetFolderId
    };
    if (this.isInspectionInOffline(id)) {
      return this.factory.importPoints(data);
    }
    return this.apiService
      .post(url, data)
      .pipe(switchMap((inspection: Inspection) => this.factory.addInspection(inspection).pipe(map(() => inspection))));
  }

  public getSectionEntity(inspectionId: string, sectionId: string): Observable<any> {
    const url = `/inspections/${inspectionId}/sections/${sectionId}/entity`;
    return this.apiService.get(url);
  }

  public updateSectionEntity(inspectionId: string, sectionId: string, section: any): Observable<any> {
    const url = `/inspections/${inspectionId}/sections/${sectionId}/entity`;
    return this.apiService.put(url, section);
  }

  public getSectionFromServer(inspectionId: string, sectionId: string, headers?: { [header: string]: string | string[] }): Observable<any> {
    const url = `/inspections/${inspectionId}/sections/${sectionId}`;
    return this.apiService.get(url, headers);
  }
}
